import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { FieldBlock } from '@lighthouse/block/src/blocks'
import type { ActorEvent, FieldADTValue, FieldCellValue, RelativeSelectConfig, VariableADTvalue } from '@lighthouse/core'
import { EVENT_TYPE, type FieldBlockAbstract, type FieldInputADTValue, VariableType } from '@lighthouse/core'
import {
    clearBlockValueByBlock,
    createFieldInputFileFilter,
    deleteBlockValueAtLeaf,
    FieldBlockProvider,
    fieldConvertValue,
    getEmptyFieldInputValue,
    getFieldInputError,
    getFieldInputRules,
    getRecordScopedListenerIds,
    getWithScopeId,
    getWithScopeIdAndRecordIdAndColumnId,
    notNeedBlurInputTypes,
    readCustomViewStack,
    setBlockValueAtLeaf,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomAction,
    useAtomData,
    useFormModuleContext,
    useRegisterBlockListener,
    useRegisterBlockListeners
} from '@lighthouse/shared'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMount, useUnmount, useUpdateEffect } from 'react-use'

import { pageStackOfBlockAtom, pageStackOfFormContainerBlockChangedAtom } from '@/atoms/application/state'
import { recordPoolAtom, viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { pageAtomFamily } from '@/atoms/page/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId, useLanguage } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import type { FieldInputConfigItem } from '@/hooks/useFieldBlockMethods'
import { useFieldBlockMethods } from '@/hooks/useFieldBlockMethods'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import * as srv from '@/services'
import { uploadManagerInAppParams } from '@/utils/auth'

interface FieldInputControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'defaultValue'> {
    blockData: FieldBlockAbstract
    defaultValue?: FieldCellValue
}

const FieldInputController: React.FC<FieldInputControllerProps> = ({ blockData, defaultValue }) => {
    const { config, id: blockId, title: blockTitle = '' } = blockData
    const { inputType, fieldPointer = '', breakPoint, eventList } = config
    const elementRef = useRef<HTMLDivElement>(null)
    const { run: setPageStackOfBlockAtom } = useAtomAction(pageStackOfBlockAtom)
    const { run: setPageStackOfFormContainerBlockChangedAtom } = useAtomAction(pageStackOfFormContainerBlockChangedAtom)
    const formModule = useFormModuleContext()
    const { handleActionListTrigger } = useActionTrigger(blockId)
    const { handleActionTriggerWithLoading } = useActionRunningLoadings()
    const { pageId } = useCurrentPageContext()
    const language = useLanguage()
    const stackId = useCurrentStackIdContext()
    const { scope } = useContainerBlockContext()
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const customViewContext = useCustomViewBlockContext()
    const { record: viewRecord } = customViewContext
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const id = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(blockId, scope, viewRecord?.id), [blockId, scope, viewRecord?.id])
    const blockKey = useMemo(() => getWithScopeId(blockId, scope), [blockId, scope])
    const recordId = viewRecord?.id
    const recordListenerIds = useMemo(() => {
        const scopedIds = getRecordScopedListenerIds(blockId, scope, readCustomViewStack(customViewContext))
        return scopedIds.length > 0 ? scopedIds : [blockKey]
    }, [blockId, blockKey, customViewContext, scope])
    const parseVariableDeps = useMemo(
        () =>
            [
                // eslint-disable-next-line unicorn/consistent-destructuring
                config.inputType === 'checkbox' ? config.checkbox?.describe : undefined,
                breakPoint.design?.background?.type === 'image' ? breakPoint.design?.background?.image?.resource : undefined
            ].filter(Boolean),
        [breakPoint.design?.background?.image, breakPoint.design?.background?.type, config]
    )
    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable = useParseVariableWithSubscription(parseVariableDeps)
    const parseVariable2String = useCallback(
        (v: VariableADTvalue | undefined) => {
            const field = parseVariable(v, {
                viewRecord
            })
            if (!field) {
                if (v?.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(v)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [parseVariable, viewRecord, personOptions, roleOptions, departmentOptions, getPageDataQueryVariableLabel]
    )

    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name ?? '', [])
    )
    const fieldBlockValueMap = useAtomData(
        pageStackOfBlockAtom,
        useCallback(s => s?.[stackId], [stackId])
    )

    const {
        serviceErrors,
        dataSource,
        record,
        pointer: formPointer,
        formId: parentFormId,
        reportFieldError,
        resetFieldServiceError,
        submitAttempted = false,
        onUpdateCell,
        formResetKey
    } = formModule ?? {}
    const formType = formModule?.type ?? 'field'

    const field = useMemo(() => dataSource?.schema?.[fieldPointer], [dataSource?.schema, fieldPointer])

    const [currentValue, setCurrentValue] = useState<FieldCellValue>(defaultValue ?? getEmptyFieldInputValue(inputType, field?.type))

    const [smsCode, setSmsCode] = useState<string | undefined>(undefined)
    const hasUserChangedRef = useRef(false)
    const [touched, setTouched] = useState(false)
    const [lastValidationError, setLastValidationError] = useState<ReturnType<typeof getFieldInputError> | null>(null)

    const showErrors = touched || submitAttempted
    const displayError = showErrors ? lastValidationError ?? undefined : undefined

    const validateAndReport = useCallback(
        (v: FieldInputADTValue) => {
            if (!field) {
                return
            }
            const fieldError = getFieldInputError({
                value: v,
                rule: getFieldInputRules({ config: config, id: blockId, title: blockTitle }),
                fieldType: field.type
            })
            const hasError = Object.keys(fieldError).length > 0
            setLastValidationError(hasError ? fieldError : null)
            reportFieldError?.(blockId, hasError ? fieldError : undefined)
            return fieldError
        },
        [blockId, blockTitle, config, field, reportFieldError]
    )

    useUpdateEffect(() => {
        const newValue = defaultValue ?? getEmptyFieldInputValue(inputType, field?.type)
        setCurrentValue(newValue)
        setTouched(false)
        setSmsCode(undefined)
        validateAndReport({ type: inputType, value: newValue } as FieldInputADTValue)
    }, [defaultValue, inputType, field?.type, formResetKey])

    useUpdateEffect(() => {
        const serviceError = serviceErrors?.[blockId]
        if (serviceError) {
            setLastValidationError(serviceError)
        }
    }, [serviceErrors])

    useMount(() => {
        if (!field || !reportFieldError) {
            return
        }
        validateAndReport({ type: inputType, value: currentValue } as FieldInputADTValue)
    })

    useUnmount(() => {
        reportFieldError?.(blockId, undefined)
    })

    const handleBlur = useCallback(
        (v: FieldInputADTValue) => {
            setTouched(true)
            const err = validateAndReport(v)
            if (formType === 'field' && record?.id && field && (!err || Object.keys(err).length === 0)) {
                onUpdateCell?.(config, record.id, { value: v.value, ...field })
            }
            const blurEvent = eventList?.find(ev => ev.type === EVENT_TYPE.BLUR)
            if (blurEvent) {
                handleActionListTrigger(id, blurEvent, {
                    viewRecord
                })
            }
        },
        [config, eventList, field, formType, handleActionListTrigger, id, onUpdateCell, record?.id, validateAndReport, viewRecord]
    )

    const handleFocus = useCallback(() => {
        const focusEvent = eventList?.find(ev => ev.type === EVENT_TYPE.FOCUS)
        if (focusEvent) {
            handleActionListTrigger(id, focusEvent, {
                viewRecord
            })
        }
    }, [eventList, handleActionListTrigger, id, viewRecord])

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLElement>) => {
            if (e.key.toLocaleLowerCase() === 'enter') {
                if (e.shiftKey) {
                    return
                }
                const keydownEvent = eventList?.find(ev => ev.type === EVENT_TYPE.ENTER)
                if (keydownEvent) {
                    e.preventDefault()
                    handleActionTriggerWithLoading({
                        id,
                        actorEvent: keydownEvent,
                        trigger: (id, actorEvent) =>
                            handleActionListTrigger(id, actorEvent, {
                                viewRecord
                            })
                    })
                }
            }
        },
        [eventList, handleActionListTrigger, handleActionTriggerWithLoading, id, viewRecord]
    )

    const handleChangeSmsCode = useCallback(
        (v: string) => {
            if (formType === 'form') {
                setSmsCode(v)
                setPageStackOfBlockAtom(draft => {
                    setBlockValueAtLeaf(
                        draft,
                        {
                            stackId,
                            blockId: blockKey,
                            recordId
                        },
                        {
                            id: blockId,
                            formId: parentFormId,
                            dsId: dataSource?.id,
                            fieldId: fieldPointer,
                            scope,
                            value: currentValue ?? '',
                            type: inputType,
                            fieldType: field?.type,
                            code: v,
                            blockType: 'field'
                        }
                    )
                })
                setPageStackOfFormContainerBlockChangedAtom(draft => {
                    draft[stackId] = true
                })
            }
        },
        [
            blockId,
            blockKey,
            currentValue,
            dataSource?.id,
            field?.type,
            fieldPointer,
            formType,
            inputType,
            parentFormId,
            recordId,
            scope,
            setPageStackOfBlockAtom,
            setPageStackOfFormContainerBlockChangedAtom,
            stackId
        ]
    )

    const handleClearValue = useCallback(() => {
        const emptyValue = getEmptyFieldInputValue(inputType, field?.type)
        const normalizedValue = emptyValue ?? ''
        const inputValue = { type: inputType, value: normalizedValue } as FieldInputADTValue
        hasUserChangedRef.current = true
        setCurrentValue(normalizedValue)
        if (formModule.type === 'form') {
            setPageStackOfFormContainerBlockChangedAtom(draft => {
                draft[stackId] = true
            })
        }
        if (notNeedBlurInputTypes.has(inputType)) {
            handleBlur(inputValue)
        }
    }, [field?.type, formModule.type, handleBlur, inputType, setPageStackOfFormContainerBlockChangedAtom, stackId])

    const handleChange = useCallback(
        (v: FieldInputADTValue) => {
            setTouched(true)
            hasUserChangedRef.current = true
            setCurrentValue(v.value)
            validateAndReport(v)
            resetFieldServiceError?.(blockId)
            if (formType === 'form') {
                setPageStackOfFormContainerBlockChangedAtom(draft => {
                    draft[stackId] = true
                })
            }
            if (notNeedBlurInputTypes.has(v.type)) {
                handleBlur(v)
            }

            const changeEvent = eventList?.find(ev => ev.type === EVENT_TYPE.CHANGE)
            if (changeEvent) {
                handleActionListTrigger(id, changeEvent, {
                    viewRecord
                })
            }
        },
        [
            blockId,
            eventList,
            formType,
            handleBlur,
            handleActionListTrigger,
            id,
            setPageStackOfFormContainerBlockChangedAtom,
            stackId,
            validateAndReport,
            viewRecord,
            resetFieldServiceError
        ]
    )

    const handleAddClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent) =>
            handleActionListTrigger(id, actorEvent, {
                viewRecord
            }),
        [handleActionListTrigger, viewRecord]
    )

    /** 获取关联数据源数据 start */
    const relativeDataSourceConfig = useMemo(() => {
        if (inputType === 'relativeSelect') {
            return [
                {
                    config: {
                        inputType,
                        relativeSelect: {
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            filter: config.relativeSelect?.filter,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            disabledFilter: config.relativeSelect?.disabledFilter,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            sorts: config.relativeSelect?.sorts,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            relativePointer: config.relativeSelect?.relativePointer,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            relativeFieldPointer: config.relativeSelect?.relativeFieldPointer,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            relativeShowFieldPointer: config.relativeSelect?.relativeShowFieldPointer,
                            // eslint-disable-next-line unicorn/consistent-destructuring
                            showMode: config.relativeSelect?.showMode
                        }
                    },
                    value: defaultValue,
                    blockId,
                    scope,
                    parentRecordId: viewRecord?.id
                }
            ] as FieldInputConfigItem[]
        }
        return []
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        inputType,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.filter,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.disabledFilter,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.sorts,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.relativePointer,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.relativeFieldPointer,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.relativeShowFieldPointer,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        (config as RelativeSelectConfig).relativeSelect?.showMode,
        defaultValue,
        blockId,
        scope,
        viewRecord?.id
    ])
    const { onFetchDataSource, onLoadMoreData, onFetchCascadeOptions, onFetchPersonOptions, onFetchCalendarOptions } = useFieldBlockMethods(
        relativeDataSourceConfig,
        fieldBlockValueMap
    )
    /** 获取关联数据源数据end */

    useEffect(() => {
        setPageStackOfBlockAtom(draft => {
            setBlockValueAtLeaf(
                draft,
                {
                    stackId,
                    blockId: blockKey,
                    recordId
                },
                {
                    id: blockId,
                    formId: formType === 'form' ? parentFormId : undefined,
                    dsId: dataSource?.id,
                    fieldId: fieldPointer,
                    scope,
                    value: currentValue ?? '',
                    type: inputType,
                    fieldType: field?.type,
                    code: formType === 'form' ? smsCode : undefined,
                    blockType: 'field'
                }
            )
        })
    }, [
        blockId,
        blockKey,
        currentValue,
        dataSource?.id,
        field?.type,
        fieldPointer,
        parentFormId,
        formType,
        inputType,
        setPageStackOfBlockAtom,
        stackId,
        recordId,
        scope,
        smsCode
    ])

    const focusInput = useCallback(() => {
        if (elementRef.current) {
            if (elementRef.current.querySelector('textarea')) {
                elementRef.current.querySelector('textarea')?.focus()
                return
            }
            if (elementRef.current.querySelector('input')) {
                elementRef.current.querySelector('input')?.focus()
                return
            }
        }
    }, [])

    useEffect(() => {
        return () => {
            setPageStackOfBlockAtom(draft => {
                deleteBlockValueAtLeaf(draft, {
                    stackId,
                    blockId: blockKey,
                    recordId
                })
            })
        }
    }, [blockKey, recordId, setPageStackOfBlockAtom, stackId])

    useRegisterBlockListeners(recordListenerIds, 'field', {
        clear: () => {
            setPageStackOfBlockAtom(draft => {
                deleteBlockValueAtLeaf(draft, {
                    stackId,
                    blockId: blockKey,
                    recordId
                })
            })
            handleClearValue()
        },
        focus: () => {
            focusInput()
        }
    })

    useRegisterBlockListener(
        recordId ? blockKey : undefined,
        'field',
        useMemo(
            () => ({
                clear: () => {
                    setPageStackOfBlockAtom(draft => {
                        clearBlockValueByBlock(draft, {
                            stackId,
                            blockId: blockKey
                        })
                    })
                    handleClearValue()
                },
                focus: () => {
                    if (recordId) {
                        focusInput()
                    }
                }
            }),
            [setPageStackOfBlockAtom, handleClearValue, stackId, blockKey, focusInput, recordId]
        )
    )

    const uploadyOptions = React.useMemo(
        () => ({
            info: { label: pageName, id: '', groupId: pageId },
            options: {
                ...uploadManagerInAppParams(),
                fileFilter: createFieldInputFileFilter({
                    fieldType: field?.type,
                    fieldId: field?.id,
                    config: inputType === 'file' ? config.file : undefined
                })
            }
        }),
        [config, field?.id, field?.type, inputType, pageId, pageName]
    )

    return (
        <FieldBlockProvider
            appId={appId}
            stackId={stackId}
            recordPoolAtom={recordPoolAtom}
            viewIndependentDataAtomFamily={viewIndependentDataAtomFamily}
        >
            <FieldBlock
                id={id}
                ref={elementRef}
                uploadyOptions={uploadyOptions}
                blockData={blockData}
                value={currentValue}
                dataSource={dataSource}
                dataSourceList={dataSourceList}
                blockType={formModule?.type === 'form' ? 'form' : 'field'}
                record={record}
                error={displayError}
                language={language}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                onKeyDown={handleKeyDown}
                richTextUploadOptions={uploadManagerInAppParams()}
                onFetchDataSource={onFetchDataSource}
                onLoadMoreData={onLoadMoreData}
                onFetchCascadeOptions={onFetchCascadeOptions}
                onChangeSmsCode={handleChangeSmsCode}
                onFetchSmsCode={formModule?.type === 'form' ? mobile => srv.getVerifyCode({ mobile, blockId, pageId }) : undefined}
                onFetchPersonOptions={onFetchPersonOptions}
                onRenderLabel={parseVariable2String}
                onFetchCalendarOptions={onFetchCalendarOptions}
                onAddClickEvent={handleAddClickEvent}
            />
        </FieldBlockProvider>
    )
}

export default FieldInputController
